'use client';

import { ShoppingBag } from 'lucide-react';
import { createContext, memo, useCallback, useContext, useState } from 'react';

import { useRouter } from 'next/navigation';

import {
  changeQuantityProductCartThunk,
  removeProductsFromCartThunk,
  SelectCartProductsRequest,
  selectCartProductThunk
} from '@screens/Cart';
import { ProductsWrapper } from '@screens/Cart/ui/ProductsWrapper/ProductsWrapper';
import { ACCENT } from '@shared/constants/colors';
import { ROUTES } from '@shared/constants/routes';
import { useAppDispatch } from '@shared/hooks/redux';
import { useOrderPaymentPlate } from '@shared/hooks/useOrderPaymentPlate';
import { HttpStatuses } from '@shared/types/enums/http.enum';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { WithNeedUpdateTokenProps } from '@shared/types/global';
import { ProductCardType, ProductsType } from '@shared/types/product';
import { Container } from '@shared/ui/Container';
import { PageHead } from '@shared/ui/PageHead';
import { VStack } from '@shared/ui/Stack';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { openAuthModal } from '@widgets/Auth';
import { refreshAccessToken } from '@widgets/Auth/api/refreshAccessToken/refreshAccessToken';
import { Empty } from '@widgets/Empty/Empty';
import { PaymentPlate } from '@widgets/PaymentPlate/PaymentPlate';
import { RefreshTokenState } from '@widgets/RefreshTokenState/RefreshTokenState';

import cls from './Cart.module.scss';

type CartProps = {
  cartProducts: ProductsType;
} & WithNeedUpdateTokenProps;

type CartContextType = {
  products: ProductCardType[];
  removeProducts: (productIds: string[]) => Promise<void>;
  selectProducts: (id?: string) => Promise<void>;
  changeQuantity: (id: string, quantity: number) => Promise<void>;
};

const crumbs = [
  { label: 'Главная', url: ROUTES.MAIN },
  { label: 'Корзина', url: '' }
];

const CartContext = createContext<CartContextType>({
  products: [],
  removeProducts: async () => {},
  selectProducts: async () => {},
  changeQuantity: async () => {}
});

export const Cart = memo<CartProps>(({ cartProducts, needUpdateToken }) => {
  const { push } = useRouter();

  const dispatch = useAppDispatch();

  const [products, setProducts] = useState<CartContextType['products']>(getProductCardData(cartProducts));

  const cartProductValues = Object.values(products);

  const orderPayment = useOrderPaymentPlate(products);

  const removeProducts = useCallback(
    async (productIds: string[]) => {
      const { payload } = await dispatch(removeProductsFromCartThunk(productIds));

      if (payload !== HttpStatuses.ERROR) {
        setProducts((prevState) => prevState.filter(({ id }) => !productIds.includes(id)));
      }
    },
    [dispatch]
  );

  const selectProducts = useCallback(
    async (id?: string) => {
      let selectedProducts;

      if (id) {
        const product = products.find((item) => id === item.id);

        selectedProducts = [{ productId: id, selected: !product?.selected }];
      } else {
        const isEverySelected = products.every(({ selected }) => selected);

        selectedProducts = products.reduce(
          (acc: SelectCartProductsRequest, item) => [...acc, { productId: item.id, selected: !isEverySelected }],
          []
        );
      }

      const { payload } = await dispatch(selectCartProductThunk(selectedProducts));

      if (payload !== HttpStatuses.ERROR) {
        const selectedProductsObj: Record<string, boolean> = selectedProducts.reduce(
          (acc, product) => ({ ...acc, [product.productId]: product.selected }),
          {}
        );

        setProducts((prevState) =>
          prevState.map((product) => ({
            ...product,
            selected: product.id in selectedProductsObj ? selectedProductsObj[product.id] : product.selected
          }))
        );
      }
    },
    [dispatch, products]
  );

  const changeQuantity = useCallback(
    async (id: string, quantity: number) => {
      const { payload } = await dispatch(changeQuantityProductCartThunk({ id, quantity }));

      if (payload !== HttpStatuses.ERROR) {
        setProducts((prevState) =>
          prevState.map((product) => ({ ...product, quantity: product.id === id ? quantity : product.quantity }))
        );
      }
    },
    [dispatch]
  );

  const handleClickCartTotalButton = useCallback(async () => {
    const { ok } = await refreshAccessToken();

    if (ok) {
      push(ROUTES.PLACING_ORDER);
      sendMetrics(Metrics.BEGIN_OFORMLENIE);
    } else {
      dispatch(openAuthModal());
    }
  }, [dispatch, push]);

  return (
    <CartContext.Provider value={{ products, removeProducts, selectProducts, changeQuantity }}>
      <PageHead
        title={'Корзина'}
        breadcrumbs={crumbs}
      />
      <Container>
        {cartProductValues.length ? (
          <div className={cls.wrapper}>
            <VStack>
              <ProductsWrapper />
            </VStack>
            <PaymentPlate
              buttonLabel={'Перейти к оформлению'}
              onClickButton={handleClickCartTotalButton}
              {...orderPayment}
            />
          </div>
        ) : (
          <Empty
            title={'Ваша корзина пуста'}
            description={'Добавьте интересующие вас товары в корзину'}
            icon={
              <ShoppingBag
                color={ACCENT}
                size={80}
              />
            }
          />
        )}
      </Container>
      <RefreshTokenState
        redirect={false}
        {...{ needUpdateToken }}
      />
    </CartContext.Provider>
  );
});

export const useCart = () => useContext(CartContext);
